import { useState } from 'react';
import { Copy, Trash2, Plus, ExternalLink, KeyRound, BookOpen, Webhook, ShieldCheck } from 'lucide-react';
import { trpc } from '../lib/trpc.ts';
import { Badge } from '../components/Badge.tsx';
import { useToast } from '../components/Toast.tsx';
import { useConfirm } from '../components/ConfirmDialog.tsx';
import { Modal } from '../components/Modal.tsx';
import { Reveal, Stagger, StaggerItem } from '../components/motion.tsx';
import { ALLOWED_SCOPES, type ApiScope } from '@cdvi-edge/shared';
import './Developer.css';

export function Developer() {
  const toast = useToast();
  const confirm = useConfirm();
  const utils = trpc.useUtils();
  const role = trpc.tenant.current.useQuery().data?.role;
  const isAdmin = role === 'admin' || role === 'owner';
  const keys = trpc.apiKey.list.useQuery(undefined, { retry: false });

  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<ApiScope[]>([]);
  const [secret, setSecret] = useState<{ prefix: string; token: string } | null>(null);

  const create = trpc.apiKey.create.useMutation({
    onSuccess: (res) => {
      void utils.apiKey.list.invalidate();
      setSecret({ prefix: res.prefix, token: res.token }); // shown ONCE
      setShowForm(false);
      setName('');
      setScopes([]);
    },
    onError: (e) => toast.error('Could not create key', e.message),
  });
  const revoke = trpc.apiKey.revoke.useMutation({
    onSuccess: () => void utils.apiKey.list.invalidate(),
    onError: (e) => toast.error('Could not revoke key', e.message),
  });

  const apiBase = import.meta.env.VITE_WORKER_URL ?? '';

  function toggleScope(s: ApiScope) {
    setScopes((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]));
  }

  function closeForm() {
    setShowForm(false);
    setName('');
    setScopes([]);
  }

  async function onRevoke(key: { id: string; name: string }) {
    const ok = await confirm({
      title: 'Revoke API key?',
      body: (
        <>
          Revoking <strong className="gold">{key.name}</strong> is permanent. Any app or integration
          still authenticating with it will stop working immediately.
        </>
      ),
      confirmLabel: 'Revoke key',
      danger: true,
    });
    if (ok) revoke.mutate({ id: key.id });
  }

  const keyCount = keys.data?.length ?? 0;
  const activeCount =
    keys.data?.filter(
      (k) =>
        !k.revokedAt && !(k.expiresAt && new Date(k.expiresAt).getTime() <= Date.now()),
    ).length ?? 0;

  return (
    <div className="dev stack">
      <Reveal>
        <div className="dev-hero">
          <span className="section-eyebrow">Developer · Programmatic access</span>
          <h1>
            Build on the <span className="serif-accent">CDVI Edge</span> API
          </h1>
          <p className="muted">
            Issue scoped API keys, explore the interactive reference, and wire your own systems into
            live access control.
          </p>
          <div className="dev-stats mono">
            <span>
              <span className="gold">{keyCount}</span> key{keyCount === 1 ? '' : 's'}
            </span>
            <span className="dev-stats-sep" aria-hidden="true">
              /
            </span>
            <span>
              <span className="gold">{activeCount}</span> active
            </span>
          </div>
        </div>
      </Reveal>

      <Stagger className="stack" gap={0.08}>
        <StaggerItem>
          <div className="card">
            <div className="card-header">
              <KeyRound size={16} />
              <h2>API keys</h2>
              <span className="spacer" />
              {isAdmin && (
                <button onClick={() => setShowForm(true)}>
                  <Plus size={14} /> Create key
                </button>
              )}
            </div>

            {keys.data && keys.data.length > 0 ? (
              <div className="dev-table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Key</th>
                      <th>Scopes</th>
                      <th>Status</th>
                      <th aria-label="Actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {keys.data.map((k) => {
                      const revoked = !!k.revokedAt;
                      const expired =
                        !!k.expiresAt && new Date(k.expiresAt).getTime() <= Date.now();
                      return (
                        <tr key={k.id} className={revoked ? 'dev-row-revoked' : undefined}>
                          <td>{k.name}</td>
                          <td>
                            <code className="dev-keycode">
                              cdvi_live_{k.prefix}…{k.lastFour}
                            </code>
                          </td>
                          <td>
                            <div className="row dev-scopes">
                              {k.scopes.map((s) => (
                                <Badge key={s}>{s}</Badge>
                              ))}
                            </div>
                          </td>
                          <td>
                            {revoked ? (
                              <Badge tone="danger">revoked</Badge>
                            ) : expired ? (
                              <Badge tone="warn">expired</Badge>
                            ) : (
                              <Badge tone="ok">active</Badge>
                            )}
                          </td>
                          <td className="dev-row-actions">
                            {!revoked && isAdmin && (
                              <button
                                className="ghost dev-revoke"
                                aria-label={`Revoke ${k.name}`}
                                title="Revoke key"
                                onClick={() => onRevoke({ id: k.id, name: k.name })}
                              >
                                <Trash2 size={14} />
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty dev-empty">
                <KeyRound size={28} className="icon" />
                <p>
                  {keys.isLoading
                    ? 'Loading keys…'
                    : isAdmin
                      ? 'No API keys yet. Create one to let an external app call the CDVI Edge API.'
                      : 'Only admins and owners can manage API keys.'}
                </p>
                {isAdmin && !keys.isLoading && (
                  <button onClick={() => setShowForm(true)}>
                    <Plus size={14} /> Create your first key
                  </button>
                )}
              </div>
            )}
          </div>
        </StaggerItem>

        <StaggerItem>
          <div className="card hoverable dev-docs">
            <div className="card-header">
              <BookOpen size={16} />
              <h2>Documentation</h2>
            </div>
            <p className="muted">
              Interactive API reference (OpenAPI 3.1) with a built-in <em className="serif-accent">try it</em>{' '}
              console.
            </p>
            <div className="row">
              <button
                className="secondary"
                onClick={() => window.open(`${apiBase}/v1/reference`, '_blank', 'noopener')}
              >
                <ExternalLink size={14} /> Open API reference
              </button>
            </div>
          </div>
        </StaggerItem>

        <StaggerItem>
          <div className="card dev-soon">
            <div className="card-header">
              <Webhook size={16} />
              <h2>Webhooks</h2>
              <span className="spacer" />
              <Badge tone="warn">coming soon</Badge>
            </div>
            <p className="muted">
              Push delivery of events to your own endpoint arrives in a later release.
            </p>
          </div>
        </StaggerItem>
      </Stagger>

      {/* ── Create-key modal ───────────────────────────────────────────────── */}
      <Modal
        open={showForm}
        onClose={closeForm}
        dismissable={!create.isPending}
        title={
          <span className="row" style={{ gap: 8 }}>
            <KeyRound size={18} className="gold" /> Create API key
          </span>
        }
        maxWidth={520}
        footer={
          <>
            <button type="button" className="ghost" onClick={closeForm} disabled={create.isPending}>
              Cancel
            </button>
            <button
              type="submit"
              form="dev-create-key"
              disabled={create.isPending || scopes.length === 0}
            >
              {create.isPending ? 'Creating…' : 'Create key'}
            </button>
          </>
        }
      >
        <form
          id="dev-create-key"
          className="stack dev-form"
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate({ name: name || 'API key', scopes });
          }}
        >
          <label className="dev-field">
            <span className="section-eyebrow">Key name</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Zapier sync"
              autoFocus
            />
          </label>

          <div className="dev-field">
            <span className="section-eyebrow">Scopes</span>
            <p className="dim dev-field-hint">
              Grant only what this integration needs. At least one scope is required.
            </p>
            <div className="dev-scope-grid">
              {ALLOWED_SCOPES.map((s) => {
                const on = scopes.includes(s);
                return (
                  <label key={s} className={`dev-scope${on ? ' is-on' : ''}`}>
                    <input type="checkbox" checked={on} onChange={() => toggleScope(s)} />
                    <span className="mono">{s}</span>
                  </label>
                );
              })}
            </div>
          </div>
        </form>
      </Modal>

      {/* ── One-time secret reveal modal ───────────────────────────────────── */}
      <Modal
        open={!!secret}
        onClose={() => setSecret(null)}
        title={
          <span className="row" style={{ gap: 8 }}>
            <ShieldCheck size={18} className="gold" /> Copy now — shown once
          </span>
        }
        maxWidth={560}
        footer={
          <>
            <button
              className="secondary"
              onClick={() => {
                if (!secret) return;
                void navigator.clipboard.writeText(secret.token);
                toast.success('Copied');
              }}
            >
              <Copy size={14} /> Copy key
            </button>
            <button onClick={() => setSecret(null)}>Done</button>
          </>
        }
      >
        <p className="muted dev-secret-note">
          Store this secret somewhere safe. For your security it{' '}
          <strong className="gold">will not be shown again</strong> after you close this dialog.
        </p>
        {secret && <pre className="dev-secret">{secret.token}</pre>}
      </Modal>
    </div>
  );
}
